import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { canManageReport } from "@/profitability/access";
import { type DocumentLinks, DocumentsTab } from "@/profitability/documents";
import type { RecordObject } from "@/profitability/records/objects";
import {
  profitabilityNewRunPath,
  profitabilityPeriodPath,
  profitabilityTemplatePanePath,
} from "@/profitability/routes";
import type {
  ProfitabilityReport,
  ProfitabilityRun,
  ReportOutput,
} from "@/profitability/types";

/**
 * The file list and the open file, until the period that owns them is in.
 * One row per input the template declares. A template with no inputs holds
 * only the preview.
 */
export const DocumentsSkeleton = ({ rows }: { rows: number }) => (
  <Stack
    gap={6}
    align="stretch"
    className="w-full min-w-0 lg:min-h-0 lg:flex-1 lg:flex-row"
    aria-busy
    role="status"
  >
    <span className="sr-only">Loading documents</span>
    {rows > 0 && (
      <Stack
        gap={2}
        align="stretch"
        className="lg:w-72 lg:shrink-0"
        aria-hidden
      >
        {Array.from({ length: rows }, (_, row) => (
          <Skeleton key={row} className="h-10 w-full rounded-lg" />
        ))}
      </Stack>
    )}
    <Skeleton className="h-96 min-w-0 flex-1 rounded-xl" aria-hidden />
  </Stack>
);

/**
 * The Documents tab of the template page: the period's files, with links
 * that keep the page's period and comparison, an upload into this period
 * for someone who may run the template, and Ask Volca in the page's chat.
 */
export const TemplateDocuments = ({
  businessId,
  report,
  run,
  periodId,
  compare,
  output,
  objects,
  onAsk,
}: {
  businessId: string;
  report: ProfitabilityReport;
  run: ProfitabilityRun;
  periodId: string | null;
  compare: string | null;
  output: ReportOutput | undefined;
  objects: RecordObject[];
  onAsk: (question: string) => void;
}) => {
  const canUpload =
    canManageReport(report) && report.published && !report.archived_at;
  const search = new URLSearchParams();
  if (periodId) search.set("period", periodId);
  if (compare) search.set("compare", compare);
  const kept = search.toString() ? `?${search.toString()}` : "";
  const links: DocumentLinks = {
    upload: (slotKey) =>
      canUpload
        ? profitabilityNewRunPath(
            report.id,
            run.start_date && run.end_date
              ? {
                  start_date: run.start_date,
                  end_date: run.end_date,
                  input: slotKey,
                  // The period's other files stay as they are.
                  from: run.id,
                }
              : null,
          )
        : null,
    record: (datasetId) => {
      const view = output?.panes.find(
        (pane) => pane.dataset === datasetId && pane.surface === "records",
      );
      return view
        ? `${profitabilityTemplatePanePath(report.id, view.id)}${kept}`
        : null;
    },
    period: (runId, reportId, documentId) =>
      `${profitabilityPeriodPath(reportId ?? report.id, runId, "documents")}&doc=${encodeURIComponent(documentId)}`,
  };
  const unresolved =
    (output?.reconciliation?.unmatched ?? 0) +
    (output?.reconciliation?.conflicting ?? 0);
  return (
    <Stack gap={3} align="stretch" className="min-h-0 flex-1">
      {unresolved > 0 && (
        <Text role="status">
          {unresolved} input rows have unmatched or conflicting records. Ask
          Volca to inspect the reconciliation decisions.
        </Text>
      )}
      <DocumentsTab
        businessId={businessId}
        run={run}
        slots={report.inputs ?? []}
        output={output}
        objects={objects}
        links={links}
        canUpload={canUpload}
        onAsk={onAsk}
      />
    </Stack>
  );
};
